import { useMemo } from 'react';
import type { Options } from 'uplot';
import { ChartCanvas, readCssVar } from './charts/ChartCanvas';
import { ChartColorFallback } from '@/tokens/chart-color-fallbacks';

export type ComparisonOverlayProps = {
  /** Baseline spectrum (healthy reference). */
  baseline: number[];
  /** Current spectrum sample. Must match baseline length. */
  current: number[];
  /** Maximum frequency at the right edge. */
  maxHz: number;
  /** Label for the baseline series — defaults to "Baseline". */
  baselineLabel?: string;
  /** Label for the current series — defaults to "Current". */
  currentLabel?: string;
  /** y-axis unit (e.g. "g rms"). */
  unit?: string;
  state?: 'default' | 'loading';
};

/**
 * Overlay two spectra so the operator can spot rises against a
 * known-good baseline. Rendered with uPlot:
 *   - drag-to-zoom on the frequency axis
 *   - crosshair + tooltip with baseline, current, and Δ readouts
 *   - bins where current > baseline get a warn-bg fill drawn via a
 *     drawClear hook, so rises pop visually
 */
export function ComparisonOverlay({
  baseline,
  current,
  maxHz,
  baselineLabel = 'Baseline',
  currentLabel = 'Current',
  unit = 'g rms',
  state = 'default',
}: ComparisonOverlayProps) {
  const { data, peak, deltaCount } = useMemo(() => {
    const n = Math.min(baseline.length, current.length);
    const xs = new Float64Array(n);
    const step = n > 1 ? maxHz / (n - 1) : 0;
    let p = 0;
    let dc = 0;
    for (let i = 0; i < n; i++) {
      xs[i] = i * step;
      if (baseline[i] > p) p = baseline[i];
      if (current[i] > p) p = current[i];
      if (current[i] > baseline[i]) dc++;
    }
    return {
      data: [xs, Float64Array.from(baseline), Float64Array.from(current)] as [
        Float64Array,
        Float64Array,
        Float64Array,
      ],
      peak: p || 1,
      deltaCount: dc,
    };
  }, [baseline, current, maxHz]);

  const options = useMemo<Omit<Options, 'width' | 'height'>>(() => {
    const baselineStroke = readCssVar('c-gray-700', ChartColorFallback.gray700);
    const currentStroke = readCssVar('c-info', ChartColorFallback.info);
    const deltaFill = readCssVar('c-warn-bg', ChartColorFallback.warn);
    const axisColor = readCssVar('c-gray-700', ChartColorFallback.gray700);
    const gridColor = readCssVar('c-gray-300', ChartColorFallback.gray300);

    return {
      // uPlot gotcha: a static `range: [min, max]` overrides any
      // user-set scale on every commit, silently breaking drag-to-zoom
      // and dblclick reset. Initial view is set in the `init` hook
      // below so drag/dblclick own the scale after construction.
      scales: {
        x: { time: false },
        y: {},
      },
      axes: [
        {
          stroke: axisColor,
          grid: { stroke: gridColor, width: 0.5 },
          values: (_u, vals) => vals.map((v) => `${v.toFixed(0)} Hz`),
        },
        {
          stroke: axisColor,
          grid: { stroke: gridColor, width: 0.5 },
          size: 60,
          values: (_u, vals) =>
            vals.map((v) => (v < 1 ? v.toFixed(3) : v.toPrecision(3))),
        },
      ],
      cursor: {
        x: true,
        y: true,
        drag: { x: true, y: false, setScale: true },
      },
      series: [
        { label: 'Hz' },
        {
          label: baselineLabel,
          stroke: baselineStroke,
          width: 1,
          dash: [4, 3],
          value: (_u, raw) =>
            raw == null ? '—' : raw < 1 ? raw.toFixed(3) : raw.toPrecision(3),
        },
        {
          label: currentLabel,
          stroke: currentStroke,
          width: 1.3,
          value: (_u, raw) =>
            raw == null ? '—' : raw < 1 ? raw.toFixed(3) : raw.toPrecision(3),
        },
      ],
      hooks: {
        init: [
          (u) => {
            u.setScale('x', { min: 0, max: maxHz });
            u.setScale('y', { min: 0, max: Math.max(0.001, peak * 1.1) });
          },
        ],
        drawClear: [
          (u) => {
            // Shade bins where current > baseline with warn-bg so the
            // overlay's signal (which bands are rising) is visible
            // even without reading the trace shapes.
            const xs = u.data[0] as Float64Array;
            const bs = u.data[1] as Float64Array;
            const cs = u.data[2] as Float64Array;
            const xMin = u.scales.x.min!;
            const xMax = u.scales.x.max!;
            u.ctx.save();
            u.ctx.fillStyle = deltaFill;
            u.ctx.globalAlpha = 0.5;
            for (let i = 0; i < xs.length; i++) {
              const x = xs[i];
              if (x < xMin || x > xMax) continue;
              if (cs[i] > bs[i]) {
                const px = u.valToPos(x, 'x', true);
                const yTop = u.valToPos(cs[i], 'y', true);
                const yBot = u.valToPos(bs[i], 'y', true);
                u.ctx.fillRect(px - 1, yTop, 2, yBot - yTop);
              }
            }
            u.ctx.restore();
          },
        ],
      },
    };
  }, [baselineLabel, currentLabel, maxHz, peak]);

  if (state === 'loading' || baseline.length === 0 || current.length === 0) {
    return (
      <div
        role="img"
        aria-label="Spectrum comparison loading"
        aria-busy={state === 'loading'}
        className="flex h-45 items-center justify-center rounded-r-card border border-gray-300 bg-gray-50 text-fs-meta text-gray-700"
        style={{ minHeight: '11.25rem' }}
      >
        {state === 'loading' ? 'Loading comparison…' : 'No comparison data'}
      </div>
    );
  }

  const a11yLabel = `Spectrum comparison: ${baselineLabel} vs ${currentLabel}, ${baseline.length} bins, peak ${peak.toFixed(2)} ${unit}, ${deltaCount} bins above baseline. Drag to zoom, double-click to reset.`;

  return (
    <figure className="rounded-r-card border border-gray-300 bg-gray-50 p-sp-3">
      <ChartCanvas
        data={data}
        options={options}
        aspect={3.4}
        ariaLabel={a11yLabel}
      />
      <figcaption className="mt-sp-1 flex justify-between text-fs-eyebrow uppercase tracking-[0.14em] text-gray-700">
        <span>
          <span className="inline-block h-sp-1 w-sp-3 bg-gray-700" /> {baselineLabel}
        </span>
        <span>
          <span className="inline-block h-sp-1 w-sp-3 bg-info" /> {currentLabel}
        </span>
        <span>
          {deltaCount} bin{deltaCount === 1 ? '' : 's'} rising · 0–{maxHz} Hz · {unit}
        </span>
      </figcaption>
    </figure>
  );
}
